import React, { useState } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/lib/supabase';
import { BackButton } from '@/components/ui/back-button';
import { 
  Zap, 
  Battery, 
  AlertTriangle, 
  CheckCircle2, 
  Scale,
  ArrowRight,
  Save
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

interface EnergyLevel {
  range: [number, number];
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  message: string;
}

const energyLevels: EnergyLevel[] = [
  {
    range: [80, 100],
    title: "Alignement parfait",
    description: "Votre énergie est à son apogée",
    icon: <CheckCircle2 className="w-8 h-8 text-green-600" />,
    color: "text-green-600",
    bgColor: "bg-green-50",
    message: "Vous êtes parfaitement aligné(e) avec votre design énergétique. C'est le moment idéal pour des activités importantes ou créatives."
  },
  {
    range: [60, 79],
    title: "Bonne énergie",
    description: "Votre énergie est stable et positive",
    icon: <Zap className="w-8 h-8 text-blue-600" />,
    color: "text-blue-600",
    bgColor: "bg-blue-50",
    message: "Votre énergie est bien équilibrée. Continuez vos activités tout en restant attentif à vos besoins."
  },
  {
    range: [40, 59],
    title: "Énergie moyenne",
    description: "Votre énergie est en équilibre",
    icon: <Scale className="w-8 h-8 text-yellow-600" />,
    color: "text-yellow-600",
    bgColor: "bg-yellow-50",
    message: "Votre énergie est stable mais modérée. Privilégiez les tâches légères et prenez des pauses régulières."
  },
  {
    range: [20, 39],
    title: "Baisse d'énergie",
    description: "Votre énergie diminue",
    icon: <Battery className="w-8 h-8 text-orange-600" />,
    color: "text-orange-600",
    bgColor: "bg-orange-50",
    message: "Votre énergie est en baisse. Il est recommandé de ralentir et de vous recentrer."
  },
  {
    range: [0, 19],
    title: "Alerte : Désalignement",
    description: "Votre énergie est très basse",
    icon: <AlertTriangle className="w-8 h-8 text-red-600" />,
    color: "text-red-600",
    bgColor: "bg-red-50",
    message: "Votre énergie est critique. Prenez du repos et évitez les activités intenses."
  }
];

export default function TestAntigravite() {
  const [energyValue, setEnergyValue] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const { toast } = useToast();
  const { currentUser } = useAuth();

  const getCurrentLevel = () => {
    return energyLevels.find(level => 
      energyValue >= level.range[0] && energyValue <= level.range[1]
    ) || energyLevels[0];
  };

  const handleSave = async () => {
    if (!currentUser) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Vous devez être connecté pour enregistrer vos résultats.",
      });
      return;
    }

    try {
      const level = getCurrentLevel();
      const recommendations = {
        toDo: energyValue >= 60 ? [
          "Profitez de cette énergie pour des projets importants",
          "Engagez-vous dans des activités créatives",
          "Partagez votre énergie avec les autres"
        ] : energyValue >= 40 ? [
          "Maintenez un rythme modéré",
          "Prenez des pauses régulières",
          "Écoutez vos besoins"
        ] : [
          "Privilégiez le repos",
          "Évitez les situations stressantes",
          "Recentrez-vous sur vos besoins essentiels"
        ],
        toAvoid: energyValue >= 60 ? [
          "Ne vous dispersez pas",
          "Évitez le surmenage",
          "Ne négligez pas vos limites"
        ] : energyValue >= 40 ? [
          "Évitez les décisions importantes",
          "Ne forcez pas votre rythme",
          "Limitez les interactions intenses"
        ] : [
          "Ne prenez pas d'engagements",
          "Évitez les situations stressantes",
          "Ne vous forcez pas à socialiser"
        ]
      };

      const { error } = await supabase
        .from('energy_history')
        .insert([{
          user_id: currentUser.id,
          energy_level: energyValue,
          energy_status: level.title,
          recommendations,
        }]);

      if (error) throw error;

      toast({
        title: "Test sauvegardé",
        description: "Votre niveau d'énergie a été enregistré.",
      });
    } catch (error) {
      console.error('Error saving energy test:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible d'enregistrer le test. Veuillez réessayer.",
      });
    }
  };

  const level = getCurrentLevel();

  return (
    <div className="min-h-screen pt-20 px-4 bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100">
      <div className="max-w-4xl mx-auto space-y-6">
        <BackButton to="/projector-watch" />
        
        <Card className="backdrop-blur-sm bg-white/90">
          <CardHeader>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Zap className="w-6 h-6 text-blue-600" />
              Test Antigravité
            </h1>
            <p className="text-gray-600">
              Évaluez votre niveau d'énergie actuel
            </p>
          </CardHeader>
          <CardContent className="space-y-8">
            {/* Energy Slider */}
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Niveau d'énergie</span>
                <span className={`text-lg font-bold ${level.color}`}>{energyValue}%</span>
              </div>
              
              <div className="relative">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={energyValue}
                  onChange={(e) => setEnergyValue(parseInt(e.target.value))}
                  onMouseDown={() => setIsDragging(true)}
                  onMouseUp={() => setIsDragging(false)}
                  onTouchStart={() => setIsDragging(true)}
                  onTouchEnd={() => setIsDragging(false)}
                  className="w-full h-3 appearance-none rounded-full outline-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, 
                      ${level.color.replace('text-', '')} 0%, 
                      ${level.color.replace('text-', '')} ${energyValue}%, 
                      #e5e7eb ${energyValue}%, 
                      #e5e7eb 100%)`
                  }}
                />
                <AnimatePresence>
                  {isDragging && (
                    <motion.div
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="absolute -top-12 left-1/2 transform -translate-x-1/2"
                    >
                      <div className={`px-3 py-1 rounded-full ${level.color} ${level.bgColor} font-bold`}>
                        {energyValue}%
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Current Status */}
            <motion.div
              key={level.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className={`p-6 rounded-lg ${level.bgColor}`}
            >
              <div className="flex items-start gap-4">
                <div className="shrink-0">
                  {level.icon}
                </div>
                <div>
                  <h2 className={`text-xl font-bold ${level.color} mb-2`}>
                    {level.title}
                  </h2>
                  <p className="text-gray-700">{level.message}</p>
                </div>
              </div>
            </motion.div>

            {/* Recommendations */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-700">Recommandations :</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Card className={`${level.bgColor} border-none`}>
                  <CardContent className="p-4">
                    <h4 className={`font-medium ${level.color} mb-2`}>À faire</h4>
                    <ul className="space-y-2 text-sm">
                      {energyValue >= 60 ? (
                        <>
                          <li>• Profitez de cette énergie pour des projets importants</li>
                          <li>• Engagez-vous dans des activités créatives</li>
                          <li>• Partagez votre énergie avec les autres</li>
                        </>
                      ) : energyValue >= 40 ? (
                        <>
                          <li>• Maintenez un rythme modéré</li>
                          <li>• Prenez des pauses régulières</li>
                          <li>• Écoutez vos besoins</li>
                        </>
                      ) : (
                        <>
                          <li>• Privilégiez le repos</li>
                          <li>• Évitez les situations stressantes</li>
                          <li>• Recentrez-vous sur vos besoins essentiels</li>
                        </>
                      )}
                    </ul>
                  </CardContent>
                </Card>

                <Card className={`${level.bgColor} border-none`}>
                  <CardContent className="p-4">
                    <h4 className={`font-medium ${level.color} mb-2`}>À éviter</h4>
                    <ul className="space-y-2 text-sm">
                      {energyValue >= 60 ? (
                        <>
                          <li>• Ne vous dispersez pas</li>
                          <li>• Évitez le surmenage</li>
                          <li>• Ne négligez pas vos limites</li>
                        </>
                      ) : energyValue >= 40 ? (
                        <>
                          <li>• Évitez les décisions importantes</li>
                          <li>• Ne forcez pas votre rythme</li>
                          <li>• Limitez les interactions intenses</li>
                        </>
                      ) : (
                        <>
                          <li>• Ne prenez pas d'engagements</li>
                          <li>• Évitez les situations stressantes</li>
                          <li>• Ne vous forcez pas à socialiser</li>
                        </>
                      )}
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-between gap-4">
              <Button
                variant="outline"
                onClick={() => setEnergyValue(50)}
                className="flex-1"
              >
                Réinitialiser
              </Button>
              <Button
                onClick={handleSave}
                className="flex-1 gap-2"
              >
                <Save className="w-4 h-4" />
                Enregistrer
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}